/**
 * pages/builder/routes.jsx
 * ------------------------
 * Lazy-loaded route wrappers for the builder console.
 *
 * Builder gets its own first-class `/builder/*` route tree (distinct
 * from `/dealer/*`) so navigation, sidebar, and bottom-nav can be
 * project-first instead of listing-first. Legacy `/builder?tab=…` URLs
 * continue to redirect via `BuilderEntry`.
 *
 * v2.71.43 — Initial.
 */

import React, { lazy, Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { DashboardSkeleton } from "../../components/Skeletons";

const BuilderOverview = lazy(() => import("./BuilderOverview"));
const BuilderInquiries = lazy(() => import("./BuilderInquiries"));
const BuilderListings = lazy(() => import("./BuilderListings"));
const BuilderMarketplaceInsights = lazy(() => import("./BuilderMarketplaceInsights"));
const BuilderAddChoice = lazy(() => import("./BuilderAddChoice"));

const wrap = (Comp) =>
  function BuilderWrap(props) {
    return (
      <Suspense fallback={<DashboardSkeleton stats={4} rows={5} />}>
        <Comp {...props} />
      </Suspense>
    );
  };

export const BuilderOverviewRoute  = wrap(BuilderOverview);
export const BuilderInquiriesRoute = wrap(BuilderInquiries);
export const BuilderListingsRoute  = wrap(BuilderListings);
export const BuilderMarketplaceRoute = wrap(BuilderMarketplaceInsights);
export const BuilderAddRoute       = wrap(BuilderAddChoice);

/**
 * BuilderEntry: bare `/builder` lands users on the new overview, while
 * preserving the legacy `?tab=X` redirect for old bookmarks. Wired in
 * App.js — replaces the previous DealerEntry-for-builder mapping.
 */
export const BUILDER_TAB_TO_ROUTE = {
  overview:    "/builder/overview",
  projects:    "/builder/projects",
  listings:    "/builder/listings",
  submit:      "/builder/add",        // v2.71.65 — now lands on choice screen
  add:         "/builder/add",
  inquiries:   "/builder/inquiries",
  enquiries:   "/builder/inquiries",
  leads:       "/builder/inquiries",
  brochures:   "/builder/brochures",
  updates:     "/builder/updates",
  visits:      "/builder/visits",
  analytics:   "/builder/analytics",
  marketplace: "/builder/marketplace",
  plan:        "/dealer/plan",
  kyc:         "/dealer/kyc",
  notifications: "/builder/notifications",
  profile:     "/builder/profile",
};

export function BuilderEntry() {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get("tab");
  const target = (tab && BUILDER_TAB_TO_ROUTE[tab]) || "/builder/overview";
  return <Navigate to={target} replace />;
}
